import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Input, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import moment from 'moment';

import { showErrorMessage } from '../../../../helpers/utils/ui';
import { ymGenerationsApi } from '../../../../shared/common/api/endpoints/ymGenerationsApi';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';
import {
    YmGenerationRow,
    YmGenerationsAccount,
    YmGenerationsListRequest,
    YmGenerationsPagination,
    YmGenerationsSortField,
    YmGenerationsTaskDict,
    YmGenerationsUploadStatusDict
} from '../types/generations';

const { Text } = Typography;

// Формат даты-времени для отображения (как в остальных таблицах проекта)
const formatDateTime = (value: string | null): string => {
    if (!value) return '—';
    const parsed = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed
        .toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
        .replace(',', '');
};

// Маппинг ответа antd-сортировки на допустимые поля бэкенда
const SORTABLE_FIELDS: YmGenerationsSortField[] = [
    'task_id',
    'conversion_datetime',
    'created_datetime',
    'upload_status_id',
    'upload_datetime'
];

const DEFAULT_FILTERS: YmGenerationsListRequest = {
    page: 1,
    pageSize: 10,
    sortField: 'created_datetime',
    sortDirection: 'desc'
};

const ACCOUNTS_SEARCH_LIMIT = 50;

const YmGenerationsTable: React.FC = () => {
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);

    const [ filters, setFilters ] = useState<YmGenerationsListRequest>(DEFAULT_FILTERS);
    const [ rows, setRows ] = useState<YmGenerationRow[]>([]);
    const [ pagination, setPagination ] = useState<YmGenerationsPagination | null>(null);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ showFilters, setShowFilters ] = useState(false);

    // Справочники
    const [ tasks, setTasks ] = useState<YmGenerationsTaskDict[]>([]);
    const [ uploadStatuses, setUploadStatuses ] = useState<YmGenerationsUploadStatusDict[]>([]);

    // Аккаунты (lazy-поиск)
    const [ accountOptions, setAccountOptions ] = useState<YmGenerationsAccount[]>([]);
    const [ accountsLoading, setAccountsLoading ] = useState(false);
    // Накопленные подписи выбранных аккаунтов, чтобы они не пропадали при новом поиске
    const accountLabelsRef = useRef<Map<number, string>>(new Map());

    // Локальное значение поля «Содержимое» (для дебаунса)
    const [ contentInput, setContentInput ] = useState('');

    const accountLabel = useCallback((account: YmGenerationsAccount): string => {
        return account.email || account.name || String(account.account_id);
    }, []);

    // Загрузка справочников — один раз при монтировании
    useEffect(() => {
        const loadDictionaries = async () => {
            try {
                const { data } = await ymGenerationsApi.getDictionaries();
                setTasks(Array.isArray(data?.data?.tasks) ? data.data.tasks : []);
                setUploadStatuses(
                    Array.isArray(data?.data?.upload_statuses) ? data.data.upload_statuses : []
                );
            } catch (error) {
                console.error('Ошибка загрузки справочников конверсий:', error);
                showErrorMessage('Не удалось загрузить справочники фильтров.');
            }
        };
        loadDictionaries();
    }, []);

    // Загрузка аккаунтов с сервера (первые записи или по строке поиска)
    const fetchAccounts = useCallback(
        async (search: string) => {
            try {
                setAccountsLoading(true);
                const { data } = await ymGenerationsApi.getAccounts({
                    search,
                    limit: ACCOUNTS_SEARCH_LIMIT
                });
                const list = Array.isArray(data?.data) ? data.data : [];
                list.forEach((account) => {
                    accountLabelsRef.current.set(account.account_id, accountLabel(account));
                });
                setAccountOptions(list);
            } catch (error) {
                console.error('Ошибка загрузки аккаунтов:', error);
            } finally {
                setAccountsLoading(false);
            }
        },
        [ accountLabel ]
    );

    const debouncedFetchAccounts = useMemo(
        () => debounce((search: string) => fetchAccounts(search), 300),
        [ fetchAccounts ]
    );

    useEffect(() => {
        return () => {
            debouncedFetchAccounts.cancel();
        };
    }, [ debouncedFetchAccounts ]);

    // Загрузка таблицы при изменении фильтров или смене школы (school_id берётся на бэке из JWT)
    useEffect(() => {
        const loadList = async () => {
            try {
                setIsLoading(true);
                const { data } = await ymGenerationsApi.getList(filters);
                setRows(Array.isArray(data?.data) ? data.data : []);
                setPagination(data?.pagination || null);
            } catch (error) {
                console.error('Ошибка загрузки списка конверсий:', error);
                showErrorMessage('Произошла ошибка при загрузке конверсий, повторите попытку позже.');
                setRows([]);
                setPagination(null);
            } finally {
                setIsLoading(false);
            }
        };
        loadList();
    }, [ filters, selectedSchool?.id ]);

    // Любое изменение фильтра сбрасывает страницу на 1
    const handleFilterChange = (patch: Partial<YmGenerationsListRequest>) => {
        setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
    };

    // Дебаунс применения поля «Содержимое»
    const debouncedContent = useMemo(
        () =>
            debounce((value: string) => {
                handleFilterChange({ generation_content: value || undefined });
            }, 400),
        []
    );

    useEffect(() => {
        return () => {
            debouncedContent.cancel();
        };
    }, [ debouncedContent ]);

    const handleAccountSearch = (search: string) => {
        debouncedFetchAccounts(search.trim());
    };

    const resetFilters = () => {
        setContentInput('');
        setFilters({ ...DEFAULT_FILTERS, pageSize: filters.pageSize });
    };

    const hasActiveFilters = !!(
        filters.task_ids?.length ||
        filters.account_ids?.length ||
        filters.upload_status_id !== undefined ||
        filters.generation_content ||
        filters.conversion_datetime_from ||
        filters.conversion_datetime_to ||
        filters.created_datetime_from ||
        filters.created_datetime_to ||
        filters.upload_datetime_from ||
        filters.upload_datetime_to
    );

    // Серверная сортировка/пагинация
    const handleTableChange = (paginationConfig: any, _tableFilters: any, sorter: any) => {
        const sortField =
            sorter?.order && SORTABLE_FIELDS.includes(sorter.columnKey)
                ? (sorter.columnKey as YmGenerationsSortField)
                : 'created_datetime';
        const sortDirection: 'asc' | 'desc' = sorter?.order === 'ascend' ? 'asc' : 'desc';

        setFilters((prev) => ({
            ...prev,
            page: paginationConfig.current || 1,
            pageSize: paginationConfig.pageSize || prev.pageSize,
            sortField,
            sortDirection
        }));
    };

    // Опции селекта аккаунтов: текущие найденные + ранее выбранные (по накопленным подписям)
    const accountSelectOptions = useMemo(() => {
        const map = new Map<number, string>();
        accountOptions.forEach((account) => {
            map.set(account.account_id, accountLabel(account));
        });
        (filters.account_ids || []).forEach((id) => {
            if (!map.has(id)) {
                map.set(id, accountLabelsRef.current.get(id) || String(id));
            }
        });
        return Array.from(map.entries()).map(([ value, label ]) => ({ value, label }));
    }, [ accountOptions, filters.account_ids, accountLabel ]);

    const columns: ColumnsType<YmGenerationRow> = [
        {
            title: 'Задача',
            dataIndex: 'task_name',
            key: 'task_id',
            width: 200,
            sorter: true,
            render: (value, record) => value || record.task_id || '—'
        },
        {
            title: 'Аккаунт',
            dataIndex: 'account_id',
            key: 'account_id',
            width: 120,
            render: (value) => value ?? '—'
        },
        {
            title: 'Дата конверсии',
            dataIndex: 'conversion_datetime',
            key: 'conversion_datetime',
            width: 170,
            sorter: true,
            render: (value) => formatDateTime(value)
        },
        {
            title: 'Дата создания',
            dataIndex: 'created_datetime',
            key: 'created_datetime',
            width: 170,
            sorter: true,
            defaultSortOrder: 'descend',
            render: (value) => formatDateTime(value)
        },
        {
            title: 'Статус выгрузки',
            dataIndex: 'upload_status_id',
            key: 'upload_status_id',
            width: 150,
            align: 'center',
            sorter: true,
            render: (value: number) =>
                value === 1 ? (
                    <Tag color="green">Отправлено</Tag>
                ) : (
                    <Tag color="default">Не отправлено</Tag>
                )
        },
        {
            title: 'Дата выгрузки',
            dataIndex: 'upload_datetime',
            key: 'upload_datetime',
            width: 170,
            sorter: true,
            render: (value) => formatDateTime(value)
        },
        {
            title: 'Содержимое',
            dataIndex: 'generation_content',
            key: 'generation_content',
            width: 260,
            render: (value) => value || '—'
        }
    ];

    const scrollX = columns.reduce((sum, col) => sum + ((col as any).width || 100), 0);

    return (
        <div>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => {
                        if (showFilters) {
                            resetFilters();
                        }
                        setShowFilters(!showFilters);
                    }}
                >
                    {showFilters ? 'Очистить и скрыть фильтры' : 'Показать фильтры'}
                </Text>
            </div>

            {showFilters && (
                <Card size="small" style={{ marginBottom: 24, border: '1px solid #d9d9d9', borderRadius: '8px' }}>
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Row gutter={[ 16, 16 ]}>
                            <Col span={6}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>Задача</Text>
                                    <Select
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        placeholder="Выберите задачи"
                                        value={filters.task_ids}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                        }
                                        onChange={(value) =>
                                            handleFilterChange({ task_ids: value?.length ? value : undefined })
                                        }
                                        options={tasks.map((task) => ({ value: task.id, label: task.task_name }))}
                                        style={{ width: '100%' }}
                                        allowClear
                                        dropdownMatchSelectWidth={false}
                                    />
                                </Space>
                            </Col>
                            <Col span={6}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>Аккаунт</Text>
                                    <Select
                                        mode="multiple"
                                        maxTagCount="responsive"
                                        placeholder="Поиск по email, имени или ID"
                                        value={filters.account_ids}
                                        filterOption={false}
                                        onSearch={handleAccountSearch}
                                        onFocus={() => {
                                            if (accountOptions.length === 0) {
                                                fetchAccounts('');
                                            }
                                        }}
                                        onChange={(value) =>
                                            handleFilterChange({ account_ids: value?.length ? value : undefined })
                                        }
                                        notFoundContent={accountsLoading ? 'Загрузка…' : 'Не найдено'}
                                        loading={accountsLoading}
                                        options={accountSelectOptions}
                                        style={{ width: '100%' }}
                                        allowClear
                                        dropdownMatchSelectWidth={false}
                                    />
                                </Space>
                            </Col>
                            <Col span={6}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>Статус выгрузки</Text>
                                    <Select
                                        placeholder="Все"
                                        value={filters.upload_status_id}
                                        onChange={(value) =>
                                            handleFilterChange({
                                                upload_status_id: value === undefined ? undefined : value
                                            })
                                        }
                                        options={uploadStatuses.map((status) => ({
                                            value: status.id,
                                            label: status.name
                                        }))}
                                        style={{ width: '100%' }}
                                        allowClear
                                        dropdownMatchSelectWidth={false}
                                    />
                                </Space>
                            </Col>
                            <Col span={6}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>Содержимое</Text>
                                    <Input
                                        placeholder="Поиск по содержимому"
                                        value={contentInput}
                                        allowClear
                                        onChange={(event) => {
                                            const { value } = event.target;
                                            setContentInput(value);
                                            debouncedContent(value);
                                        }}
                                    />
                                </Space>
                            </Col>
                        </Row>
                        <Row gutter={[ 16, 16 ]}>
                            <Col span={6}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>Дата конверсии</Text>
                                    <DatePicker.RangePicker
                                        format="DD.MM.YYYY"
                                        placeholder={[ 'Дата от', 'Дата до' ]}
                                        style={{ width: '100%' }}
                                        value={
                                            filters.conversion_datetime_from && filters.conversion_datetime_to
                                                ? [
                                                    moment(filters.conversion_datetime_from),
                                                    moment(filters.conversion_datetime_to)
                                                ]
                                                : undefined
                                        }
                                        onChange={(dates) =>
                                            handleFilterChange({
                                                conversion_datetime_from: dates?.[0]?.format('YYYY-MM-DD'),
                                                conversion_datetime_to: dates?.[1]?.format('YYYY-MM-DD')
                                            })
                                        }
                                    />
                                </Space>
                            </Col>
                            <Col span={6}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>Дата создания</Text>
                                    <DatePicker.RangePicker
                                        format="DD.MM.YYYY"
                                        placeholder={[ 'Дата от', 'Дата до' ]}
                                        style={{ width: '100%' }}
                                        value={
                                            filters.created_datetime_from && filters.created_datetime_to
                                                ? [
                                                    moment(filters.created_datetime_from),
                                                    moment(filters.created_datetime_to)
                                                ]
                                                : undefined
                                        }
                                        onChange={(dates) =>
                                            handleFilterChange({
                                                created_datetime_from: dates?.[0]?.format('YYYY-MM-DD'),
                                                created_datetime_to: dates?.[1]?.format('YYYY-MM-DD')
                                            })
                                        }
                                    />
                                </Space>
                            </Col>
                            <Col span={6}>
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Text>Дата выгрузки</Text>
                                    <DatePicker.RangePicker
                                        format="DD.MM.YYYY"
                                        placeholder={[ 'Дата от', 'Дата до' ]}
                                        style={{ width: '100%' }}
                                        value={
                                            filters.upload_datetime_from && filters.upload_datetime_to
                                                ? [
                                                    moment(filters.upload_datetime_from),
                                                    moment(filters.upload_datetime_to)
                                                ]
                                                : undefined
                                        }
                                        onChange={(dates) =>
                                            handleFilterChange({
                                                upload_datetime_from: dates?.[0]?.format('YYYY-MM-DD'),
                                                upload_datetime_to: dates?.[1]?.format('YYYY-MM-DD')
                                            })
                                        }
                                    />
                                </Space>
                            </Col>
                        </Row>
                        {hasActiveFilters && (
                            <Row>
                                <Col>
                                    <Button
                                        onClick={resetFilters}
                                        style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                                    >
                                        Очистить фильтры
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </Space>
                </Card>
            )}

            <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
                <Table
                    columns={columns}
                    dataSource={rows}
                    rowKey={(_record, index) => `${filters.page}-${index ?? 0}`}
                    size="small"
                    bordered
                    scroll={{ x: scrollX }}
                    loading={{
                        spinning: isLoading,
                        indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />
                    }}
                    onChange={handleTableChange}
                    pagination={{
                        current: filters.page,
                        pageSize: filters.pageSize,
                        total: pagination?.total || 0,
                        showSizeChanger: true,
                        pageSizeOptions: [ '10', '20', '50', '100' ],
                        showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
                        style: { paddingRight: 16 }
                    }}
                />
            </div>
        </div>
    );
};

export default YmGenerationsTable;
